import { myExpirationsQuery } from "@/api/me";
import { PageInset } from "@/components/PageInset";
import { TokenAmount } from "@/components/TokenAmount";
import { TopNavigation, TopNavigationBackButton } from "@/components/ui/TopNavigation";
import { locale } from "@common/utils/i18n";
import { Box, Flex, QueryHandler, Span } from "@givenwell/components";
import { MyExpiration } from "@givenwell/marketplace-api";
import { CSS } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/_auth/_app/wallet/expiring")({
  component: Page,
});

function Page() {
  const expirations = useQuery(myExpirationsQuery);
  const canGoBack = useCanGoBack();
  const router = useRouter();
  const navigate = useNavigate();

  return (
    <Box css={{ width: "100%", maxW: 600, mx: "auto" }}>
      <TopNavigation
        title="Expiring Points"
        left={
          <TopNavigationBackButton
            onClick={e => {
              if (canGoBack) {
                e.preventDefault();
                router.history.back();
              } else {
                navigate({ to: "/wallet", replace: true });
              }
            }}
          />
        }
      />
      <PageInset>
        <Box
          css={{
            backgroundColor: colors.cream,
          }}
        >
          <QueryHandler
            query={expirations}
            success={({ data }) => {
              const sortedExpirations = [...(data || [])].sort(
                (a, b) => new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime(),
              );
              return (
                <Box css={{ spaceY: 16 }}>
                  {sortedExpirations.map((expiration, i) => (
                    <ExpirationLineItem key={i} expiration={expiration} />
                  ))}
                </Box>
              );
            }}
          />
        </Box>
        <Box css={{ pb: "var(--safe-area-inset-bottom)" }} />
      </PageInset>
    </Box>
  );
}

function ExpirationLineItem({ expiration, css }: { expiration: MyExpiration; css?: CSS }) {
  const expiringAmount = expiration.token_amount - expiration.spent_amount;

  const expiringPercentage = (expiringAmount / expiration.token_amount) * 100;

  return (
    <Box css={{ py: 16, spaceY: 12 }}>
      <Flex css={{ color: colors.text2, fontScale: "sm", width: "100%", justifyContent: "space-between", ...css }}>
        <TokenAmount amount={expiringAmount} showIcon inline size="sm" showFreeForZero={false} />
        <Span>
          expires on{" "}
          <Span css={{ color: colors.navy, weight: 500 }}>
            {new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(
              new Date(expiration.expires_at),
            )}
          </Span>
        </Span>
      </Flex>
      <Box css={{ width: "100%", height: 8, backgroundColor: colors.warmGrey, borderRadius: 4 }}>
        <Box
          css={{
            width: `${expiringPercentage}%`,
            height: "100%",
            borderRadius: "inherit",
            backgroundColor: colors.navy,
          }}
        />
      </Box>
    </Box>
  );
}
